'use client'

import * as PopoverPrimitive from '@radix-ui/react-popover'
import { Highlight } from '@tiptap/extension-highlight'
import { Image as ImageExtension } from '@tiptap/extension-image'
import { TextAlign } from '@tiptap/extension-text-align'
import { Color, FontFamily, FontSize, TextStyle } from '@tiptap/extension-text-style'
import { type Editor, EditorContent, useEditor, useEditorState } from '@tiptap/react'
import { BubbleMenu } from '@tiptap/react/menus'
import { StarterKit } from '@tiptap/starter-kit'
import { Select, type SelectOption } from '@trustoo/components/Select/v2'
import { Tooltip } from '@trustoo/components/Tooltip/v2'
import {
    AlignCenter,
    AlignLeft,
    Bold01,
    Dotpoints01,
    Image01,
    Italic01,
    Link01,
    Stars02,
    Type01,
    Underline01,
} from '@untitledui/icons'
import clsx from 'clsx'
import {
    type ButtonHTMLAttributes,
    forwardRef,
    type KeyboardEvent,
    type MouseEvent,
    type ReactNode,
    useEffect,
    useId,
    useRef,
    useState,
} from 'react'

export type TextEditorType = 'default' | 'floating-toolbar'
export type TextEditorSize = 'sm' | 'md'
export type TextEditorToolbar = 'simple' | 'advanced'

type UrlRequest = () => Promise<string | null> | string | null

export interface TextEditorProps {
    value?: string
    defaultValue?: string
    onChange?: (html: string) => void
    type?: TextEditorType
    size?: TextEditorSize
    toolbar?: TextEditorToolbar
    placeholder?: string
    label?: ReactNode
    hintText?: ReactNode
    maxLength?: number
    disabled?: boolean
    onRequestLink?: UrlRequest
    onRequestImage?: UrlRequest
    onGenerate?: () => void
    className?: string
    'aria-label'?: string
}

type ToolbarTone = 'light' | 'dark'

type ToolbarItem =
    | 'bold'
    | 'italic'
    | 'underline'
    | 'color'
    | 'align-left'
    | 'align-center'
    | 'dot-points'
    | 'link'
    | 'image'
    | 'generate'
    | 'divider'

const FIXED_TOOLBAR_ITEMS: Record<TextEditorToolbar, ToolbarItem[]> = {
    simple: [
        'bold',
        'italic',
        'underline',
        'divider',
        'color',
        'divider',
        'align-left',
        'align-center',
        'dot-points',
    ],
    advanced: [
        'bold',
        'italic',
        'underline',
        'divider',
        'color',
        'divider',
        'align-left',
        'align-center',
        'dot-points',
        'divider',
        'link',
        'image',
        'divider',
        'generate',
    ],
}

const FLOATING_TOOLBAR_ITEMS: Record<TextEditorToolbar, ToolbarItem[]> = {
    simple: ['bold', 'italic', 'underline', 'align-left', 'align-center', 'link'],
    advanced: FIXED_TOOLBAR_ITEMS.advanced,
}

interface SizeClasses {
    root: string
    box: string
    content: string
    placeholder: string
    floating: string
    defaultFontSize: string
}

const SIZE_CLASSES: Record<TextEditorSize, SizeClasses> = {
    sm: {
        root: 'gap-spacing-md',
        box: 'max-h-[350px] rounded-radius-md',
        content: 'text-sm [&_.ProseMirror]:min-h-32 [&_.ProseMirror]:p-spacing-xl',
        placeholder: 'left-4 top-4 text-sm',
        floating: 'rounded-radius-md p-spacing-xs',
        defaultFontSize: '12px',
    },
    md: {
        root: 'gap-spacing-lg',
        box: 'max-h-[440px] rounded-radius-xl',
        content: 'text-md [&_.ProseMirror]:min-h-40 [&_.ProseMirror]:p-spacing-2xl',
        placeholder: 'left-5 top-5 text-md',
        floating: 'rounded-radius-xl p-spacing-md',
        defaultFontSize: '14px',
    },
}

const TONE_BUTTON_CLASSES: Record<ToolbarTone, { idle: string; active: string }> = {
    light: {
        idle: 'bg-base-transparent text-foreground-fg-quaternary hover:bg-background-bg-primary_hover hover:text-foreground-fg-quaternary_hover',
        active: 'bg-background-bg-primary_hover text-foreground-fg-secondary',
    },
    dark: {
        idle: 'bg-base-transparent text-neutral-500 hover:bg-alpha-white-10 hover:text-neutral-300',
        active: 'bg-background-bg-brand-dark text-neutral-300',
    },
}

const TONE_DIVIDER_CLASSES: Record<ToolbarTone, string> = {
    light: 'border-border-primary',
    dark: 'border-neutral-800',
}

const FLOATING_SURFACE_CLASSES: Record<TextEditorToolbar, string> = {
    simple: 'rounded-radius-xl border border-solid border-neutral-800 bg-background-bg-primary-solid p-spacing-sm shadow-lg',
    advanced: 'bg-background-bg-primary shadow-lg ring-1 ring-alpha-black-10',
}

const FLOATING_TONE: Record<TextEditorToolbar, ToolbarTone> = {
    simple: 'dark',
    advanced: 'light',
}

interface Swatch {
    key: string
    label: string
    className: string
}

const COLOR_SWATCHES: Swatch[] = [
    { key: 'neutral-950', label: 'Black', className: 'bg-neutral-950' },
    { key: 'neutral-800', label: 'Dark gray', className: 'bg-neutral-800' },
    { key: 'neutral-700', label: 'Gray 700', className: 'bg-neutral-700' },
    { key: 'neutral-600', label: 'Gray 600', className: 'bg-neutral-600' },
    { key: 'neutral-500', label: 'Gray 500', className: 'bg-neutral-500' },
    { key: 'neutral-400', label: 'Gray 400', className: 'bg-neutral-400' },
    { key: 'neutral-300', label: 'Light gray', className: 'bg-neutral-300' },
    { key: 'white', label: 'White', className: 'bg-base-white' },
    { key: 'green-600', label: 'Green', className: 'bg-green-600' },
    { key: 'brand-600', label: 'Blue', className: 'bg-brand-600' },
    { key: 'brand-800', label: 'Dark blue', className: 'bg-brand-800' },
    { key: 'brand-400', label: 'Light blue', className: 'bg-brand-400' },
    { key: 'amber-500', label: 'Amber', className: 'bg-amber-500' },
    { key: 'red-500', label: 'Pink red', className: 'bg-red-500' },
    { key: 'red-600', label: 'Red', className: 'bg-red-600' },
    { key: 'orange-600', label: 'Orange', className: 'bg-orange-600' },
]

const FONT_FAMILY_OPTIONS: SelectOption[] = [
    { value: 'Aspekta', label: 'Aspekta' },
    { value: 'Inter', label: 'Inter' },
    { value: 'Arial', label: 'Arial' },
    { value: 'Georgia', label: 'Georgia' },
    { value: 'Courier New', label: 'Courier New' },
]

const FONT_SIZE_OPTIONS: SelectOption[] = [
    { value: '12px', label: '12px' },
    { value: '14px', label: '14px' },
    { value: '16px', label: '16px' },
    { value: '18px', label: '18px' },
    { value: '20px', label: '20px' },
    { value: '24px', label: '24px' },
]

const HEX_PATTERN = /^(?:[0-9a-f]{3}|[0-9a-f]{6})$/i

const ICON_SIZE = 20

interface EditorUiState {
    bold: boolean
    italic: boolean
    underline: boolean
    bulletList: boolean
    alignLeft: boolean
    alignCenter: boolean
    link: boolean
    color: string | null
    fontFamily: string | null
    fontSize: string | null
    characterCount: number
    isEmpty: boolean
}

const EMPTY_UI_STATE: EditorUiState = {
    bold: false,
    italic: false,
    underline: false,
    bulletList: false,
    alignLeft: false,
    alignCenter: false,
    link: false,
    color: null,
    fontFamily: null,
    fontSize: null,
    characterCount: 0,
    isEmpty: true,
}

const readString = (attributes: Record<string, unknown>, key: string): string | null => {
    const attributeValue = attributes[key]
    return typeof attributeValue === 'string' ? attributeValue : null
}

const readUiState = (editor: Editor | null): EditorUiState => {
    if (editor) {
        const textStyle: Record<string, unknown> = editor.getAttributes('textStyle')
        return {
            bold: editor.isActive('bold'),
            italic: editor.isActive('italic'),
            underline: editor.isActive('underline'),
            bulletList: editor.isActive('bulletList'),
            alignLeft: editor.isActive({ textAlign: 'left' }),
            alignCenter: editor.isActive({ textAlign: 'center' }),
            link: editor.isActive('link'),
            color: readString(textStyle, 'color'),
            fontFamily: readString(textStyle, 'fontFamily'),
            fontSize: readString(textStyle, 'fontSize'),
            characterCount: editor.getText().length,
            isEmpty: editor.isEmpty,
        }
    }
    return EMPTY_UI_STATE
}

const visibleItems = (
    items: ToolbarItem[],
    available: { link: boolean; image: boolean; generate: boolean },
): ToolbarItem[] => {
    const filtered = items.filter((item) => {
        if (item === 'link') {
            return available.link
        }
        if (item === 'image') {
            return available.image
        }
        if (item === 'generate') {
            return available.generate
        }
        return true
    })
    const collapsed: ToolbarItem[] = []
    filtered.forEach((item) => {
        const previous = collapsed[collapsed.length - 1]
        if (item === 'divider' && (previous === undefined || previous === 'divider')) {
            return
        }
        collapsed.push(item)
    })
    while (collapsed[collapsed.length - 1] === 'divider') {
        collapsed.pop()
    }
    return collapsed
}

const keepSelection = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
}

interface ToolbarButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
    tone: ToolbarTone
    active?: boolean
    swatch?: boolean
}

const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>(
    ({ tone, active = false, swatch = false, className, children, ...rest }, ref) => {
        const toneClasses = TONE_BUTTON_CLASSES[tone]
        return (
            <button
                ref={ref}
                type="button"
                className={clsx(
                    'inline-flex shrink-0 cursor-pointer items-center justify-center rounded-radius-sm border-0',
                    'transition-colors duration-100 ease-linear',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                    swatch ? 'p-spacing-md' : 'p-spacing-sm',
                    active ? toneClasses.active : toneClasses.idle,
                    className,
                )}
                {...rest}>
                {children}
            </button>
        )
    },
)
ToolbarButton.displayName = 'TextEditorToolbarButton'

const ToolbarDivider = ({ tone }: { tone: ToolbarTone }) => (
    <span aria-hidden="true" className="flex h-8 shrink-0 items-center px-spacing-sm">
        <span
            className={clsx('h-5 w-0 border-0 border-l border-solid', TONE_DIVIDER_CLASSES[tone])}
        />
    </span>
)

interface ColorPickerProps {
    tone: ToolbarTone
    color: string | null
    disabled: boolean
    onApply: (color: string) => void
}

const ColorPicker = ({ tone, color, disabled, onApply }: ColorPickerProps) => {
    const [open, setOpen] = useState(false)
    const [selectedKey, setSelectedKey] = useState<string | null>(null)
    const [custom, setCustom] = useState('')
    const customIsValid = HEX_PATTERN.test(custom)
    const customColor = customIsValid ? `#${custom}` : undefined

    const applySwatch = (event: MouseEvent<HTMLButtonElement>, key: string) => {
        const computed = window.getComputedStyle(event.currentTarget).backgroundColor
        setSelectedKey(key)
        onApply(computed)
    }

    const applyCustom = () => {
        if (customColor) {
            setSelectedKey(null)
            onApply(customColor)
        }
    }

    const handleCustomKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            applyCustom()
        }
    }

    return (
        <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
            <Tooltip title="Text color" arrow={false}>
                <PopoverPrimitive.Trigger asChild>
                    <ToolbarButton
                        tone={tone}
                        swatch
                        disabled={disabled}
                        aria-label="Text color"
                        aria-haspopup="dialog">
                        <span
                            aria-hidden="true"
                            className={clsx(
                                'block size-4 rounded-radius-full',
                                color === null && 'bg-neutral-900',
                                open
                                    ? 'ring-2 ring-neutral-900 ring-offset-1'
                                    : 'ring-1 ring-inset ring-alpha-black-10',
                            )}
                            style={color === null ? undefined : { backgroundColor: color }}
                        />
                    </ToolbarButton>
                </PopoverPrimitive.Trigger>
            </Tooltip>
            <PopoverPrimitive.Content
                side="bottom"
                align="start"
                sideOffset={6}
                collisionPadding={16}
                onOpenAutoFocus={(event) => event.preventDefault()}
                className="z-50 flex flex-col gap-spacing-lg rounded-radius-xl border border-solid border-border-secondary bg-background-bg-primary p-spacing-md font-body shadow-lg">
                <div
                    className="grid grid-cols-8 gap-spacing-xs"
                    role="group"
                    aria-label="Text colors">
                    {COLOR_SWATCHES.map((swatch) => {
                        const isSelected = selectedKey === swatch.key
                        return (
                            <button
                                key={swatch.key}
                                type="button"
                                aria-label={swatch.label}
                                aria-pressed={isSelected}
                                onMouseDown={keepSelection}
                                onClick={(event) => applySwatch(event, swatch.key)}
                                className={clsx(
                                    'size-5 cursor-pointer rounded-radius-full border-0 p-spacing-none',
                                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
                                    swatch.className,
                                    isSelected
                                        ? 'ring-2 ring-neutral-900 ring-offset-1'
                                        : 'ring-1 ring-inset ring-alpha-black-10',
                                )}
                            />
                        )
                    })}
                </div>
                <div className="flex items-center gap-spacing-lg">
                    <span className="shrink-0 text-md font-semibold text-text-primary">Custom</span>
                    <div className="flex min-w-0 flex-1 items-center gap-spacing-md rounded-radius-md border border-solid border-border-primary bg-background-bg-primary px-spacing-md py-spacing-xs shadow-xs focus-within:border-border-brand focus-within:ring-1 focus-within:ring-inset focus-within:ring-focus-ring">
                        <span
                            aria-hidden="true"
                            className={clsx(
                                'block size-4 shrink-0 rounded-radius-full ring-1 ring-inset ring-alpha-black-10',
                                customIsValid ? undefined : 'bg-background-bg-quaternary',
                            )}
                            style={customColor ? { backgroundColor: customColor } : undefined}
                        />
                        <input
                            value={custom}
                            maxLength={6}
                            aria-label="Custom hex color"
                            onChange={(event) => setCustom(event.target.value.replace('#', ''))}
                            onKeyDown={handleCustomKeyDown}
                            onBlur={applyCustom}
                            className="w-20 min-w-0 border-0 bg-base-transparent p-spacing-none font-body text-md uppercase text-text-primary outline-none"
                        />
                    </div>
                </div>
            </PopoverPrimitive.Content>
        </PopoverPrimitive.Root>
    )
}

export const TextEditor = forwardRef<HTMLDivElement, TextEditorProps>(
    (
        {
            value,
            defaultValue,
            onChange,
            type = 'default',
            size = 'md',
            toolbar = 'advanced',
            placeholder,
            label,
            hintText,
            maxLength,
            disabled = false,
            onRequestLink,
            onRequestImage,
            onGenerate,
            className,
            'aria-label': ariaLabel,
        },
        ref,
    ) => {
        const generatedId = useId()
        const labelId = `${generatedId}-label`
        const hintId = `${generatedId}-hint`
        const s = SIZE_CLASSES[size]
        const isFloating = type === 'floating-toolbar'

        const onChangeRef = useRef(onChange)
        onChangeRef.current = onChange
        const maxLengthRef = useRef(maxLength)
        maxLengthRef.current = maxLength

        const editorAttributes: Record<string, string> = {
            role: 'textbox',
            'aria-multiline': 'true',
            class: 'outline-none',
        }
        if (label) {
            editorAttributes['aria-labelledby'] = labelId
        }
        if (ariaLabel) {
            editorAttributes['aria-label'] = ariaLabel
        }
        if (hintText || maxLength !== undefined) {
            editorAttributes['aria-describedby'] = hintId
        }

        const editor = useEditor({
            immediatelyRender: false,
            editable: !disabled,
            content: value ?? defaultValue ?? '',
            extensions: [
                StarterKit.configure({
                    link: { openOnClick: false, autolink: true },
                }),
                TextAlign.configure({ types: ['heading', 'paragraph'] }),
                ImageExtension,
                Highlight,
                TextStyle,
                Color,
                FontFamily,
                FontSize,
            ],
            editorProps: {
                attributes: editorAttributes,
                handleTextInput: (view, from, to, text) => {
                    const limit = maxLengthRef.current
                    if (limit === undefined) {
                        return false
                    }
                    const nextLength = view.state.doc.textContent.length - (to - from) + text.length
                    return nextLength > limit
                },
            },
            onUpdate: ({ editor: current }) => {
                onChangeRef.current?.(current.getHTML())
            },
        })

        const ui =
            useEditorState({
                editor,
                selector: ({ editor: current }) => readUiState(current),
            }) ?? EMPTY_UI_STATE

        useEffect(() => {
            if (editor && value !== undefined && value !== editor.getHTML()) {
                editor.commands.setContent(value, { emitUpdate: false })
            }
        }, [editor, value])

        useEffect(() => {
            if (editor) {
                editor.setEditable(!disabled)
            }
        }, [editor, disabled])

        const controlsDisabled = disabled || editor === null

        const requestLink = async () => {
            if (editor && onRequestLink) {
                const url = await Promise.resolve(onRequestLink())
                if (url === null) {
                    return
                }
                if (url === '') {
                    editor.chain().focus().extendMarkRange('link').unsetLink().run()
                } else {
                    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
                }
            }
        }

        const requestImage = async () => {
            if (editor && onRequestImage) {
                const url = await Promise.resolve(onRequestImage())
                if (url) {
                    editor.chain().focus().setImage({ src: url }).run()
                }
            }
        }

        const applyColor = (color: string) => {
            editor?.chain().focus().setColor(color).run()
        }

        const available = {
            link: onRequestLink !== undefined,
            image: onRequestImage !== undefined,
            generate: onGenerate !== undefined,
        }

        const renderCommand = (
            tone: ToolbarTone,
            key: string,
            title: string,
            icon: ReactNode,
            onClick: () => void,
            active?: boolean,
        ) => (
            <Tooltip key={key} title={title} arrow={false}>
                <ToolbarButton
                    tone={tone}
                    active={active === true}
                    aria-label={title}
                    aria-pressed={active}
                    disabled={controlsDisabled}
                    onMouseDown={keepSelection}
                    onClick={onClick}>
                    {icon}
                </ToolbarButton>
            </Tooltip>
        )

        const renderItem = (item: ToolbarItem, index: number, tone: ToolbarTone): ReactNode => {
            const key = `${item}-${index}`
            const chain = () => editor?.chain().focus()
            switch (item) {
                case 'bold':
                    return renderCommand(
                        tone,
                        key,
                        'Bold ⌘B',
                        <Bold01 size={ICON_SIZE} />,
                        () => chain()?.toggleBold().run(),
                        ui.bold,
                    )
                case 'italic':
                    return renderCommand(
                        tone,
                        key,
                        'Italic ⌘I',
                        <Italic01 size={ICON_SIZE} />,
                        () => chain()?.toggleItalic().run(),
                        ui.italic,
                    )
                case 'underline':
                    return renderCommand(
                        tone,
                        key,
                        'Underline ⌘U',
                        <Underline01 size={ICON_SIZE} />,
                        () => chain()?.toggleUnderline().run(),
                        ui.underline,
                    )
                case 'align-left':
                    return renderCommand(
                        tone,
                        key,
                        'Left align',
                        <AlignLeft size={ICON_SIZE} />,
                        () => chain()?.setTextAlign('left').run(),
                        ui.alignLeft,
                    )
                case 'align-center':
                    return renderCommand(
                        tone,
                        key,
                        'Center align',
                        <AlignCenter size={ICON_SIZE} />,
                        () => chain()?.setTextAlign('center').run(),
                        ui.alignCenter,
                    )
                case 'dot-points':
                    return renderCommand(
                        tone,
                        key,
                        'Dot points',
                        <Dotpoints01 size={ICON_SIZE} />,
                        () => chain()?.toggleBulletList().run(),
                        ui.bulletList,
                    )
                case 'link':
                    return renderCommand(
                        tone,
                        key,
                        'Link ⌘K',
                        <Link01 size={ICON_SIZE} />,
                        () => {
                            requestLink().catch(() => null)
                        },
                        ui.link,
                    )
                case 'image':
                    return renderCommand(
                        tone,
                        key,
                        'Insert image',
                        <Image01 size={ICON_SIZE} />,
                        () => {
                            requestImage().catch(() => null)
                        },
                    )
                case 'generate':
                    return renderCommand(tone, key, 'Generate', <Stars02 size={ICON_SIZE} />, () =>
                        onGenerate?.(),
                    )
                case 'color':
                    return (
                        <ColorPicker
                            key={key}
                            tone={tone}
                            color={ui.color}
                            disabled={controlsDisabled}
                            onApply={applyColor}
                        />
                    )
                default:
                    return <ToolbarDivider key={key} tone={tone} />
            }
        }

        const renderButtons = (items: ToolbarItem[], tone: ToolbarTone) =>
            visibleItems(items, available).map((item, index) => renderItem(item, index, tone))

        const fixedToolbar = isFloating ? null : (
            <div
                role="toolbar"
                aria-label="Formatting"
                className="flex flex-wrap items-center gap-x-spacing-lg gap-y-spacing-md">
                {toolbar === 'advanced' && (
                    <div className="flex items-center gap-spacing-md">
                        <div className="w-[152px]">
                            <Select
                                size="sm"
                                type="icon-leading"
                                aria-label="Font family"
                                options={FONT_FAMILY_OPTIONS}
                                value={ui.fontFamily ?? 'Aspekta'}
                                disabled={controlsDisabled}
                                leadingIcon={
                                    <Type01
                                        size={ICON_SIZE}
                                        className="text-foreground-fg-quaternary"
                                    />
                                }
                                onChange={(next) =>
                                    editor?.chain().focus().setFontFamily(next).run()
                                }
                            />
                        </div>
                        <div className="w-[88px]">
                            <Select
                                size="sm"
                                aria-label="Font size"
                                options={FONT_SIZE_OPTIONS}
                                value={ui.fontSize ?? s.defaultFontSize}
                                disabled={controlsDisabled}
                                onChange={(next) => editor?.chain().focus().setFontSize(next).run()}
                            />
                        </div>
                    </div>
                )}
                <div className="flex flex-wrap items-center gap-spacing-xxs">
                    {renderButtons(FIXED_TOOLBAR_ITEMS[toolbar], 'light')}
                </div>
            </div>
        )

        const floatingToolbar =
            isFloating && editor ? (
                <BubbleMenu
                    editor={editor}
                    options={{ placement: 'top', offset: 8 }}
                    shouldShow={({ editor: current, from, to }) =>
                        current.isEditable && from !== to
                    }>
                    <div
                        role="toolbar"
                        aria-label="Formatting"
                        className={clsx(
                            'flex items-center gap-spacing-xxs',
                            FLOATING_SURFACE_CLASSES[toolbar],
                            toolbar === 'advanced' && s.floating,
                        )}>
                        {renderButtons(FLOATING_TOOLBAR_ITEMS[toolbar], FLOATING_TONE[toolbar])}
                    </div>
                </BubbleMenu>
            ) : null

        let message: ReactNode = null
        if (hintText) {
            message = hintText
        } else if (maxLength !== undefined) {
            const remaining = Math.max(maxLength - ui.characterCount, 0)
            message = `${remaining} characters left`
        }

        return (
            <div ref={ref} className={clsx('flex w-full flex-col font-body', s.root, className)}>
                {label && (
                    <span id={labelId} className="text-sm font-medium text-text-secondary">
                        {label}
                    </span>
                )}
                {fixedToolbar}
                <div className="flex min-h-0 flex-1 flex-col gap-spacing-sm">
                    <div
                        className={clsx(
                            'relative flex min-h-0 flex-1 flex-col overflow-y-auto border border-solid border-border-primary bg-background-bg-primary shadow-xs',
                            'transition-colors duration-150 ease-in-out',
                            'focus-within:border-border-brand focus-within:ring-1 focus-within:ring-inset focus-within:ring-focus-ring',
                            s.box,
                            disabled && 'cursor-not-allowed opacity-50',
                        )}>
                        {placeholder && ui.isEmpty && (
                            <span
                                aria-hidden="true"
                                className={clsx(
                                    'pointer-events-none absolute text-text-placeholder',
                                    s.placeholder,
                                )}>
                                {placeholder}
                            </span>
                        )}
                        <EditorContent
                            editor={editor}
                            className={clsx(
                                'flex-1 font-regular text-text-primary',
                                '[&_.ProseMirror]:h-full [&_.ProseMirror]:outline-none',
                                '[&_.ProseMirror_p]:m-spacing-none [&_.ProseMirror_p+p]:mt-spacing-lg',
                                '[&_.ProseMirror_ul]:my-spacing-none [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-spacing-2xl',
                                '[&_.ProseMirror_a]:text-text-brand-secondary [&_.ProseMirror_a]:underline',
                                '[&_.ProseMirror_img]:max-w-full [&_.ProseMirror_img]:rounded-radius-md',
                                '[&_.ProseMirror_code]:rounded-radius-xs [&_.ProseMirror_code]:bg-background-bg-tertiary [&_.ProseMirror_code]:px-spacing-xs',
                                isFloating && 'selection:bg-brand-900/10',
                                s.content,
                            )}
                        />
                        {floatingToolbar}
                    </div>
                    {message && (
                        <p
                            id={hintId}
                            className="m-spacing-none text-sm font-regular text-text-tertiary">
                            {message}
                        </p>
                    )}
                </div>
            </div>
        )
    },
)
TextEditor.displayName = 'TextEditor'
